import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { secureUniqueId } from "@/utils/secureRandom";

export type NotificationType = 'system' | 'access' | 'credits' | 'trial';

export interface AppNotification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: number;
  read: boolean;
  data?: any;
}

type NotificationStore = {
  notifications: AppNotification[];
  actions: {
    addNotification: (notification: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => void;
    markAsRead: (id: string) => void;
    markAllRead: () => void;
    clearNotifications: () => void;
  };
};

export const useNotificationStore = create<NotificationStore>()(
  persist(
    (set) => ({
      notifications: [],
      actions: {
        addNotification: (notification) => {
          set((state) => ({
            notifications: [
              ...state.notifications,
              {
                ...notification,
                id: secureUniqueId(),
                timestamp: Date.now(),
                read: false,
              },
            ],
          }));
        },
        markAsRead: (id) => {
          set((state) => ({
            notifications: state.notifications.map((n) =>
              n.id === id ? { ...n, read: true } : n
            ),
          }));
        },
        markAllRead: () => {
          set((state) => ({
            notifications: state.notifications.map((n) => ({ ...n, read: true })),
          }));
        },
        clearNotifications: () => {
          set({ notifications: [] });
        },
      },
    }),
    {
      name: "notificationStore",
      storage: createJSONStorage(() => localStorage),
      // Don't persist actions — only data
      partialize: (state) => ({ notifications: state.notifications }),
    }
  )
);

export const useNotifications = () => useNotificationStore((state) => state.notifications);
export const useNotificationActions = () => useNotificationStore((state) => state.actions);
